import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useSearchParams } from "react-router";
import { Plus } from "lucide-react";
import { useGetList, useGetOne } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type {
  Company,
  Contact,
  Deal,
  DealClientPayment,
} from "@/components/atomic-crm/types";
import {
  clientDealFilter,
  clientRecordFilter,
  clientScopeCompanyId,
  clientScopeContactId,
  type ClientScope,
} from "@/modules/clients/clientScope";
import type { Proposal } from "@/modules/types";
import type { ClientInvoice } from "@/modules/types";
import { ClientTabEmpty } from "@/modules/clients/ClientContactsTab";
import {
  ClientContractsTab,
  ClientInvoicesTab,
  ClientProposalsTab,
} from "@/modules/clients/ClientTabPanels";
import {
  ClientTabAccordion,
  ClientTabAccordionSection,
} from "@/modules/clients/ClientTabAccordion";
import {
  ClientTabContentCard,
  clientTableWrapperClassName,
} from "@/modules/clients/ClientTabSectionCard";
import {
  FINANCIAL_SECTIONS,
  getValidFinancialSection,
  type FinancialSection,
} from "@/modules/clients/clientShowUtils";
import { MoneyText } from "@/lib/permissions/MoneyText";
import {
  buildInvoicePaymentRows,
  buildProjectPaymentRows,
  mergeClientFinancialPaymentRows,
} from "@/modules/clients/clientFinancialPayments";
import { useMemberCapability } from "@/components/atomic-crm/providers/commons/useMemberCapability";
import { getClientInvoiceCreatePath } from "@/app/routing";
import { CreateProposalButton } from "@/modules/proposals/CreateProposalButton";
import { DataGridTable, type GridColumn } from "@/modules/shared/dataGrid";
import type { ClientFinancialPaymentRow } from "@/modules/clients/clientFinancialPayments";
import { useOrgModules } from "@/modules/useOrgModules";

const TabLoading = () => (
  <div className="space-y-2">
    <Skeleton className="h-20 w-full" />
    <Skeleton className="h-10 w-full" />
  </div>
);

type ClientFinancialTabProps = {
  scope: ClientScope;
  counts: {
    invoices: number;
    proposals: number;
    contracts: number;
    payments: number;
  };
  syncUrl?: boolean;
};

export const ClientFinancialTab = ({
  scope,
  counts,
  syncUrl = true,
}: ClientFinancialTabProps) => {
  const companyId = clientScopeCompanyId(scope);
  const contactId = clientScopeContactId(scope);
  const [searchParams, setSearchParams] = useSearchParams();
  const canCreateInvoice = useMemberCapability("proposals.send");
  const sectionFromUrl =
    syncUrl && searchParams.get("tab") === "financial"
      ? getValidFinancialSection(searchParams.get("section"))
      : "summary";

  const [openSections, setOpenSections] = useState<string[]>(() => [
    sectionFromUrl,
  ]);

  useEffect(() => {
    if (!syncUrl) return;
    if (searchParams.get("tab") !== "financial") return;
    setOpenSections([getValidFinancialSection(searchParams.get("section"))]);
  }, [syncUrl, searchParams.get("tab"), searchParams.get("section")]);

  // Sections of apps the company doesn't have stay hidden.
  const { isEnabled: isModuleOn } = useOrgModules();

  const handleAccordionChange = (values: string[]) => {
    setOpenSections(values);
    if (!syncUrl) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", "financial");
    const primary = values[0] as FinancialSection | undefined;
    if (!primary || primary === "summary") {
      next.delete("section");
    } else if (FINANCIAL_SECTIONS.includes(primary)) {
      next.set("section", primary);
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!syncUrl) return;
    const tab = searchParams.get("tab");
    if (tab !== "financial") return;
    const rawSection = searchParams.get("section");
    if (
      rawSection &&
      !FINANCIAL_SECTIONS.includes(rawSection as FinancialSection)
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete("section");
      setSearchParams(next, { replace: true });
    }
  }, [syncUrl, searchParams, setSearchParams]);

  return (
    <ClientTabAccordion
      value={openSections}
      onValueChange={handleAccordionChange}
    >
      <ClientTabAccordionSection value="summary" title="Summary">
        <ClientTabContentCard>
          <ClientFinancialSummary scope={scope} />
        </ClientTabContentCard>
      </ClientTabAccordionSection>

      {isModuleOn("invoices") ? (
        <ClientTabAccordionSection
          value="invoices"
          title="Invoices"
          count={counts.invoices}
          action={
            canCreateInvoice ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1 px-2"
                asChild
              >
                <Link to={getClientInvoiceCreatePath(companyId, contactId)}>
                  <Plus className="size-3.5" aria-hidden />
                  New invoice
                </Link>
              </Button>
            ) : null
          }
        >
          <ClientTabContentCard flush>
            <ClientInvoicesTab scope={scope} />
          </ClientTabContentCard>
        </ClientTabAccordionSection>
      ) : null}

      {isModuleOn("proposals") ? (
        <ClientTabAccordionSection
          value="proposals"
          title="Proposals"
          count={counts.proposals}
          action={
            <CreateProposalButton
              companyId={companyId}
              contactId={contactId}
              variant="ghost"
              className="h-8 gap-1 px-2"
            />
          }
        >
          <ClientTabContentCard flush>
            <ClientProposalsTab scope={scope} />
          </ClientTabContentCard>
        </ClientTabAccordionSection>
      ) : null}

      {isModuleOn("contracts") ? (
        <ClientTabAccordionSection
          value="contracts"
          title="Contracts"
          count={counts.contracts}
        >
          <ClientTabContentCard flush>
            <ClientContractsTab scope={scope} />
          </ClientTabContentCard>
        </ClientTabAccordionSection>
      ) : null}

      {isModuleOn("invoices") ? (
        <ClientTabAccordionSection
          value="payments"
          title="Payments"
          count={counts.payments}
        >
          <ClientTabContentCard flush>
            <ClientPaymentsSection scope={scope} />
          </ClientTabContentCard>
        </ClientTabAccordionSection>
      ) : null}
    </ClientTabAccordion>
  );
};

const ClientFinancialSummary = ({ scope }: { scope: ClientScope }) => {
  const { data: deals = [], isLoading: dealsLoading } = useGetList<Deal>(
    "deals",
    {
      filter: clientDealFilter(scope),
      pagination: { page: 1, perPage: 200 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 30_000 },
  );

  const dealIds = useMemo(() => deals.map((deal) => deal.id), [deals]);
  const paymentsEnabled = dealIds.length > 0;

  const paymentsFilter = paymentsEnabled
    ? { "deal_id@in": `(${dealIds.join(",")})` }
    : { "deal_id@eq": -1 };

  const { data: payments = [], isLoading: paymentsLoading } =
    useGetList<DealClientPayment>(
      "deal_client_payments",
      {
        filter: paymentsFilter,
        pagination: { page: 1, perPage: 500 },
        sort: { field: "payment_date", order: "DESC" },
      },
      { staleTime: 30_000, enabled: paymentsEnabled },
    );

  const { data: proposals = [], isLoading: proposalsLoading } =
    useGetList<Proposal>(
      "proposals",
      {
        filter: clientRecordFilter(scope),
        pagination: { page: 1, perPage: 100 },
        sort: { field: "updated_at", order: "DESC" },
      },
      { staleTime: 30_000 },
    );

  const { data: invoices = [], isLoading: invoicesLoading } =
    useGetList<ClientInvoice>(
      "client_invoices",
      {
        filter: clientRecordFilter(scope),
        pagination: { page: 1, perPage: 100 },
        sort: { field: "issue_date", order: "DESC" },
      },
      { staleTime: 30_000 },
    );

  const totalContracted = useMemo(
    () => deals.reduce((sum, deal) => sum + Number(deal.amount ?? 0), 0),
    [deals],
  );

  const totalCollected = useMemo(
    () =>
      payments
        .filter(
          (payment) =>
            payment.status === "cleared" || payment.status === "deposited",
        )
        .reduce((sum, payment) => sum + Number(payment.amount ?? 0), 0),
    [payments],
  );

  const totalPending = useMemo(
    () =>
      payments
        .filter((payment) => payment.status === "pending")
        .reduce((sum, payment) => sum + Number(payment.amount ?? 0), 0),
    [payments],
  );

  const openProposals = useMemo(
    () =>
      proposals.filter(
        (proposal) =>
          proposal.status &&
          !["accepted", "declined", "expired"].includes(proposal.status),
      ),
    [proposals],
  );

  const activeInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.status !== "void"),
    [invoices],
  );

  const totalInvoiced = useMemo(
    () =>
      activeInvoices.reduce(
        (sum, invoice) => sum + Number(invoice.amount ?? 0),
        0,
      ),
    [activeInvoices],
  );

  const totalInvoicePaid = useMemo(
    () =>
      activeInvoices.reduce((sum, invoice) => {
        if (invoice.status === "paid") {
          return sum + Number(invoice.amount_paid ?? invoice.amount ?? 0);
        }
        return sum + Number(invoice.amount_paid ?? 0);
      }, 0),
    [activeInvoices],
  );

  const invoiceOutstanding = totalInvoiced - totalInvoicePaid;

  if (dealsLoading || paymentsLoading || proposalsLoading || invoicesLoading) {
    return <TabLoading />;
  }

  if (
    deals.length === 0 &&
    proposals.length === 0 &&
    activeInvoices.length === 0
  ) {
    return (
      <ClientTabEmpty message="No financial activity for this client yet. Ticket invoices, proposals, and project payments appear here once created." />
    );
  }

  const balance = totalContracted - totalCollected;
  const showProjectSummary = deals.length > 0;
  const showInvoiceSummary = activeInvoices.length > 0;

  return (
    <div className="space-y-4">
      {showProjectSummary ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            label="Contracted"
            value={<MoneyText value={totalContracted} />}
          />
          <SummaryCard
            label="Collected"
            value={<MoneyText value={totalCollected} />}
          />
          <SummaryCard
            label="Pending payments"
            value={<MoneyText value={totalPending} />}
          />
          <SummaryCard label="Balance" value={<MoneyText value={balance} />} />
        </div>
      ) : null}
      {showInvoiceSummary ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <SummaryCard
            label="Invoiced"
            value={<MoneyText value={totalInvoiced} />}
          />
          <SummaryCard
            label="Paid"
            value={<MoneyText value={totalInvoicePaid} />}
          />
          <SummaryCard
            label="Outstanding"
            value={<MoneyText value={invoiceOutstanding} />}
          />
        </div>
      ) : null}
      {openProposals.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          {openProposals.length} open proposal
          {openProposals.length === 1 ? "" : "s"} awaiting response.
        </p>
      ) : null}
    </div>
  );
};

const ClientPaymentsSection = ({ scope }: { scope: ClientScope }) => {
  const companyId = clientScopeCompanyId(scope);
  const contactId = clientScopeContactId(scope);
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: companyId ?? "" },
    { staleTime: 60_000, enabled: companyId != null },
  );
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: contactId ?? "" },
    { staleTime: 60_000, enabled: contactId != null },
  );

  const { data: deals = [], isLoading: dealsLoading } = useGetList<Deal>(
    "deals",
    {
      filter: clientDealFilter(scope),
      pagination: { page: 1, perPage: 200 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 30_000 },
  );

  const { data: invoices = [], isLoading: invoicesLoading } =
    useGetList<ClientInvoice>(
      "client_invoices",
      {
        filter: clientRecordFilter(scope),
        pagination: { page: 1, perPage: 200 },
        sort: { field: "paid_at", order: "DESC" },
      },
      { staleTime: 30_000 },
    );

  const dealIds = useMemo(() => deals.map((deal) => deal.id), [deals]);
  const dealsById = useMemo(
    () => Object.fromEntries(deals.map((deal) => [String(deal.id), deal])),
    [deals],
  );

  const paymentsEnabled = dealIds.length > 0;
  const paymentsFilter = paymentsEnabled
    ? { "deal_id@in": `(${dealIds.join(",")})` }
    : { "deal_id@eq": -1 };

  const { data: projectPayments = [], isLoading: projectPaymentsLoading } =
    useGetList<DealClientPayment>(
      "deal_client_payments",
      {
        filter: paymentsFilter,
        pagination: { page: 1, perPage: 500 },
        sort: { field: "payment_date", order: "DESC" },
      },
      { staleTime: 30_000, enabled: paymentsEnabled },
    );

  const payerLabel =
    (contact
      ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
      : company?.name
    )?.trim() || "Client";
  const paymentRows = useMemo(
    () =>
      mergeClientFinancialPaymentRows(
        buildInvoicePaymentRows(invoices, payerLabel),
        buildProjectPaymentRows(projectPayments, dealsById, payerLabel),
      ),
    [dealsById, invoices, payerLabel, projectPayments],
  );

  if (dealsLoading || invoicesLoading || projectPaymentsLoading) {
    return <TabLoading />;
  }

  if (paymentRows.length === 0) {
    return (
      <ClientTabEmpty message="No payments recorded yet. Stripe invoice payments and project payments appear here once collected." />
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Payment received
      </h4>
      <div className={clientTableWrapperClassName}>
        <DataGridTable
          tableId="client.payments"
          columns={PAYMENT_COLUMNS}
          rows={paymentRows}
          rowKey={(row) => row.id}
          initialSort={{ field: "date", order: "DESC" }}
        />
      </div>
    </div>
  );
};

const PAYMENT_COLUMNS: GridColumn<ClientFinancialPaymentRow>[] = [
  {
    id: "date",
    label: "Date",
    type: "date",
    sortable: true,
    filterable: true,
    alwaysVisible: true,
    value: (row) => row.sortKey,
    cellClassName: "text-muted-foreground",
    cell: (row) => row.dateLabel,
  },
  {
    id: "payer",
    label: "Payment",
    type: "text",
    sortable: true,
    filterable: true,
    value: (row) => row.payerLabel,
    cell: (row) => row.payerLabel,
  },
  {
    id: "invoice",
    label: "Invoice",
    type: "text",
    filterable: true,
    value: (row) => row.invoiceNumber ?? row.dealName ?? "",
    cell: (row) =>
      row.invoiceId ? (
        <div className="space-y-0.5">
          <Link
            to={`/billing/invoices/${row.invoiceId}/show`}
            className="link-action font-medium"
          >
            {row.invoiceNumber ?? `Invoice #${row.invoiceId}`}
          </Link>
          {row.ticketId ? (
            <div className="text-xs text-muted-foreground">
              <Link
                to={`/tickets/${row.ticketId}/show`}
                className="link-action"
              >
                Ticket #{row.ticketId}
              </Link>
            </div>
          ) : null}
        </div>
      ) : row.dealId ? (
        <Link
          to={`/deals/${row.dealId}/show`}
          className="link-action font-medium"
        >
          {row.dealName ?? `Project #${row.dealId}`}
        </Link>
      ) : (
        "—"
      ),
  },
  {
    id: "reference",
    label: "Reference",
    type: "text",
    filterable: true,
    cellClassName: "max-w-[10rem] truncate text-muted-foreground",
    cell: (row) => row.reference,
  },
  {
    id: "status",
    label: "Status",
    type: "text",
    sortable: true,
    filterable: true,
    cell: (row) => (
      <Badge variant={row.statusVariant} className="capitalize">
        {row.status}
      </Badge>
    ),
  },
  {
    id: "paymentMode",
    label: "Payment mode",
    type: "text",
    sortable: true,
    filterable: true,
    cellClassName: "capitalize text-muted-foreground",
    cell: (row) => row.paymentMode,
  },
  {
    id: "amount",
    label: "Amount",
    type: "money",
    sortable: true,
    filterable: true,
    align: "right",
    cellClassName: "font-medium text-success-text",
    cell: (row) => <MoneyText value={row.amount} />,
  },
];

const SummaryCard = ({ label, value }: { label: string; value: ReactNode }) => (
  <Card className="shadow-none">
    <CardContent className="p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </CardContent>
  </Card>
);
